import { supabase } from '../lib/supabase'
import type { Database } from '../types/database'

type Conversation = Database['public']['Tables']['conversations']['Row']
type ConversationMessage = Database['public']['Tables']['conversation_messages']['Row']

export interface ConversationWithDetails extends Conversation {
  user: {
    id: string
    name: string
    email: string
    // Carried so チャット監視 can show and filter by who the speaker is. Teachers and
    // admins have no school/grade/class, so all three are nullable.
    role?: string
    grade?: string | null
    class?: string | null
    school_id?: string | null
    school?: { id: string; name: string } | null
  }
  chat_prompt: {
    id: string
    title: string
    video_id?: string | null
    is_generic?: boolean // Add is_generic flag to filter out generic conversations
    // AI settings of the underlying prompt. Carried so a conversation resumed from
    // history keeps using the model it was configured with instead of a hardcoded
    // fallback. 'default' means "use the system default" (admin ai_default_model).
    model?: string
    temperature?: number
    max_tokens?: number
    category: {
      id: string
      name: string
      slug: string
    } | null // Allow null for generic prompts
  }
}

export interface ConversationMessageWithDetails extends ConversationMessage {
  conversation: {
    id: string
    title: string
    user: {
      id: string
      name: string
      email: string
    }
  }
}

/**
 * One recorded `instructions` string for a conversation, written by the openai-chat edge
 * function. See database/104_add_conversation_prompt_snapshots.sql.
 */
export interface ConversationPromptSnapshot {
  id: string
  conversation_id: string
  instructions: string
  applied_response_policy: string | null
  school_id: string | null
  chat_prompt_id: string | null
  model: string | null
  max_output_tokens: number | null
  reasoning_effort: string | null
  text_verbosity: string | null
  used_previous_response_id: boolean | null
  created_at: string
  school: { id: string; name: string } | null
}

export interface ConversationStats {
  totalConversations: number
  activeConversations: number
  totalMessages: number
  averageMessagesPerConversation: number
  conversationsByCategory: Array<{
    category_name: string
    count: number
  }>
  conversationsByStatus: Array<{
    status: string
    count: number
  }>
}

/**
 * The admin views embed a conversation's prompt and that prompt's category.
 *
 * `chat_prompts` has no category of its own: a prompt points at a video, and the video
 * carries `category_id`. Asking PostgREST for `chat_prompts(category:categories(...))`
 * therefore fails the whole request with PGRST200 ("Could not find a relationship
 * between 'chat_prompts' and 'categories'"), which is what left チャット監視 stuck on
 * 「会話の読み込みに失敗しました」. The category has to be reached through the video.
 */
const CONVERSATION_DETAIL_SELECT = `
  *,
  user:users(id, name, email, role, grade, class, school_id, school:schools(id, name)),
  chat_prompt:chat_prompts(
    id,
    title,
    video_id,
    is_generic,
    model,
    temperature,
    max_tokens,
    video:videos!video_id(
      id,
      title,
      category:categories!category_id(id, name, slug)
    )
  )
`

/** Same embed, with both hops required so a category filter can exclude rows. */
const CONVERSATION_DETAIL_SELECT_BY_CATEGORY = CONVERSATION_DETAIL_SELECT
  .replace('chat_prompt:chat_prompts(', 'chat_prompt:chat_prompts!inner(')
  .replace('video:videos!video_id(', 'video:videos!video_id!inner(')

interface EmbeddedPromptVideo {
  video?: { category?: ConversationWithDetails['chat_prompt']['category'] } | null
}

/**
 * Lift the video's category onto the prompt, so callers keep seeing
 * `chat_prompt.category` and stay unaware of the join path it came from.
 */
function withFlattenedCategory(rows: unknown): ConversationWithDetails[] {
  return ((rows ?? []) as Array<ConversationWithDetails & { chat_prompt: EmbeddedPromptVideo }>)
    .map((row) => {
      if (!row?.chat_prompt) return row as ConversationWithDetails
      const { video, ...prompt } = row.chat_prompt
      return {
        ...row,
        chat_prompt: { ...prompt, category: video?.category ?? null },
      } as ConversationWithDetails
    })
}

export class ConversationService {
  static async createConversation(
    userId: string,
    chatPromptId: string,
    title: string
  ): Promise<Conversation> {
    const { data, error } = await supabase
      .from('conversations')
      .insert({
        user_id: userId,
        chat_prompt_id: chatPromptId,
        title,
        status: 'active',
        message_count: 0
      })
      .select()
      .single()

    if (error) {
      console.error('Error creating conversation:', error)
      throw new Error('会話の作成に失敗しました')
    }

    return data
  }

  static async getOrCreateConversation(
    userId: string,
    chatPromptId: string,
    title: string
  ): Promise<Conversation> {
    // Resume the most recently used conversation. A user can accumulate several
    // undeleted conversations for one prompt (every 新規チャット creates another), and
    // without an explicit order Postgres is free to return them in any order -- which
    // made the resumed thread, and with it the previous_response_id sent to OpenAI,
    // effectively arbitrary from one page load to the next.
    const { data: conversations, error: fetchError } = await supabase
      .from('conversations')
      .select('*')
      .eq('user_id', userId)
      .eq('chat_prompt_id', chatPromptId)
      .neq('status', 'deleted')
      .order('updated_at', { ascending: false })
      .order('created_at', { ascending: false })
      .limit(1)

    if (fetchError) {
      console.error('Error fetching conversation:', fetchError)
      throw new Error('会話の取得に失敗しました')
    }

    const existing = conversations?.[0] || null
    if (existing) {
      return existing
    }

    // If not found, create new conversation
    return this.createConversation(userId, chatPromptId, title)
  }

  static async createMessage(
    conversationId: string,
    role: 'user' | 'assistant' | 'system',
    content: string,
    metadata?: {
      tokensUsed?: number
      modelUsed?: string
      responseTimeMs?: number
      openaiResponseId?: string
      [key: string]: any
    }
  ): Promise<ConversationMessage> {
    const { data, error } = await supabase
      .from('conversation_messages')
      .insert({
        conversation_id: conversationId,
        role,
        content,
        tokens_used: metadata?.tokensUsed || null,
        model_used: metadata?.modelUsed || null,
        response_time_ms: metadata?.responseTimeMs || null,
        openai_response_id: metadata?.openaiResponseId || null,
        metadata: metadata ? JSON.stringify(metadata) : null
      })
      .select()
      .single()

    if (error) {
      console.error('Error creating message:', error)
      throw new Error('メッセージの作成に失敗しました')
    }

    // Update conversation's last_message_at and message_count
    await this.updateConversationAfterMessage(conversationId)

    return data
  }

  static async updateConversationAfterMessage(conversationId: string): Promise<void> {
    // Get current message count
    const { count, error: countError } = await supabase
      .from('conversation_messages')
      .select('*', { count: 'exact', head: true })
      .eq('conversation_id', conversationId)

    if (countError) {
      console.error('Error counting messages:', countError)
      return
    }

    // Update conversation
    const { error: updateError } = await supabase
      .from('conversations')
      .update({
        last_message_at: new Date().toISOString(),
        message_count: count || 0
      })
      .eq('id', conversationId)

    if (updateError) {
      console.error('Error updating conversation:', updateError)
    }
  }

  /**
   * Conversations for チャット監視, narrowed the same way 生徒管理 narrows its list.
   *
   * The speaker filters (search / school / grade / class / role) all live on `users`, so
   * they need the embed to be !inner -- without it PostgREST blanks the embedded user
   * and keeps the conversation. Teachers and admins have no school, grade or class, so
   * those three filters exclude them by nature; `role` is what brings them back.
   *
   * Returns the page together with the total matching count, so the caller can paginate
   * without issuing the same filters twice.
   */
  static async getAllConversations(
    limit: number = 50,
    offset: number = 0,
    categoryId?: string,
    status?: string,
    userFilters?: {
      search?: string
      schoolId?: string
      grade?: string
      class?: string
      // A list, because the UI's 管理者 option covers both admin and super_admin
      roles?: string[]
    }
  ): Promise<{ conversations: ConversationWithDetails[]; totalCount: number }> {
    const needsUserJoin = !!(
      userFilters?.search ||
      userFilters?.schoolId ||
      userFilters?.grade ||
      userFilters?.class ||
      userFilters?.roles?.length
    )

    let select = categoryId ? CONVERSATION_DETAIL_SELECT_BY_CATEGORY : CONVERSATION_DETAIL_SELECT
    if (needsUserJoin) {
      select = select.replace('user:users(', 'user:users!inner(')
    }

    // `count: 'exact'` makes the same request report how many rows match the filters, so
    // the page and its total come back together instead of from two round trips.
    let query = supabase
      .from('conversations')
      .select(select, { count: 'exact' })
      .order('last_message_at', { ascending: false, nullsFirst: false })
      .order('created_at', { ascending: false })
      .range(offset, offset + limit - 1)

    if (categoryId) {
      // The category lives on the video behind the prompt, so the filter has to walk
      // the same path as the embed. Both hops are !inner in the by-category select, so
      // this drops non-matching conversations instead of merely blanking the embed.
      query = query.eq('chat_prompt.video.category_id', categoryId)
    }

    if (status) {
      query = query.eq('status', status)
    }

    if (userFilters?.schoolId) {
      query = query.eq('user.school_id', userFilters.schoolId)
    }
    if (userFilters?.grade) {
      query = query.eq('user.grade', userFilters.grade)
    }
    if (userFilters?.class) {
      // Prefix match, same as UserService.getUsers so 「A」 finds A組 and A-1
      query = query.ilike('user.class', `${userFilters.class}%`)
    }
    if (userFilters?.roles?.length) {
      query = query.in('user.role', userFilters.roles)
    }
    if (userFilters?.search) {
      // Scoped to the embedded resource: a top-level `or` cannot reach `user.name`
      query = query.or(
        `name.ilike.%${userFilters.search}%,email.ilike.%${userFilters.search}%`,
        { referencedTable: 'user' }
      )
    }

    const { data, error, count } = await query

    if (error) {
      console.error('Error fetching conversations:', error)
      throw new Error('会話の取得に失敗しました')
    }

    return { conversations: withFlattenedCategory(data), totalCount: count ?? 0 }
  }

  static async getConversationById(id: string): Promise<ConversationWithDetails | null> {
    const { data, error } = await supabase
      .from('conversations')
      .select(CONVERSATION_DETAIL_SELECT)
      .eq('id', id)
      .maybeSingle()

    if (error) {
      console.error('Error fetching conversation:', error)
      throw new Error('会話の取得に失敗しました')
    }

    return data ? withFlattenedCategory([data])[0] : null
  }

  /**
   * The instructions recorded for a conversation, newest first.
   *
   * Readable by admins only (RLS). An empty array is the normal answer for a chat that
   * predates the recording, so callers must not read it as a failure.
   */
  static async getPromptSnapshots(conversationId: string): Promise<ConversationPromptSnapshot[]> {
    const { data, error } = await supabase
      .from('conversation_prompt_snapshots')
      .select('*, school:schools(id, name)')
      .eq('conversation_id', conversationId)
      .order('created_at', { ascending: false })

    if (error) {
      console.error('Error fetching prompt snapshots:', error)
      throw new Error('送信プロンプトの取得に失敗しました')
    }

    return (data ?? []) as unknown as ConversationPromptSnapshot[]
  }

  static async getConversationMessages(
    conversationId: string,
    limit: number = 100,
    offset: number = 0
  ): Promise<ConversationMessage[]> {
    const { data, error } = await supabase
      .from('conversation_messages')
      .select('*')
      .eq('conversation_id', conversationId)
      .order('created_at', { ascending: true })
      .range(offset, offset + limit - 1)

    if (error) {
      console.error('Error fetching conversation messages:', error)
      throw new Error('会話メッセージの取得に失敗しました')
    }

    return data
  }

  static async getUserConversationWithMessages(
    userId: string,
    chatPromptId: string
  ): Promise<{ conversation: Conversation | null; messages: ConversationMessage[] }> {
    // Get the most recently used conversation. Several active ones can exist for the
    // same prompt, so the order has to be explicit -- see getOrCreateConversation.
    const { data: conversations, error: convError } = await supabase
      .from('conversations')
      .select('*')
      .eq('user_id', userId)
      .eq('chat_prompt_id', chatPromptId)
      .eq('status', 'active')
      .order('updated_at', { ascending: false })
      .order('created_at', { ascending: false })
      .limit(1)

    if (convError) {
      console.error('Error fetching user conversation:', convError)
      throw new Error('会話の取得に失敗しました')
    }

    const conversation = conversations?.[0] || null
    if (!conversation) {
      return { conversation: null, messages: [] }
    }

    // Get messages
    const messages = await this.getConversationMessages(conversation.id)

    return { conversation, messages }
  }

  static async getConversationWithMessages(
    conversationId: string
  ): Promise<{ conversation: Conversation | null; messages: ConversationMessage[] }> {
    const { data: conversation, error: convError } = await supabase
      .from('conversations')
      .select('*')
      .eq('id', conversationId)
      .maybeSingle()

    if (convError) {
      console.error('Error fetching conversation by id:', convError)
      throw new Error('会話の取得に失敗しました')
    }

    if (!conversation) return { conversation: null, messages: [] }

    const messages = await this.getConversationMessages(conversationId)
    return { conversation, messages }
  }

  static async getConversationStats(): Promise<ConversationStats> {
    // Get basic conversation stats
    const { data: conversationCount, error: countError } = await supabase
      .from('conversations')
      .select('id, status', { count: 'exact' })

    if (countError) {
      console.error('Error fetching conversation count:', countError)
      throw new Error('会話統計の取得に失敗しました')
    }

    // Get message count
    const { data: messageCount, error: messageError } = await supabase
      .from('conversation_messages')
      .select('id', { count: 'exact' })

    if (messageError) {
      console.error('Error fetching message count:', messageError)
      throw new Error('メッセージ統計の取得に失敗しました')
    }

    // Get conversations by category, reached through the prompt's video -- see
    // CONVERSATION_DETAIL_SELECT for why categories cannot be embedded directly.
    const { data: categoryStats, error: categoryError } = await supabase
      .from('conversations')
      .select(`
        chat_prompt:chat_prompts(
          video:videos!video_id(
            category:categories!category_id(name)
          )
        )
      `)

    if (categoryError) {
      console.error('Error fetching category stats:', categoryError)
      throw new Error('カテゴリー統計の取得に失敗しました')
    }

    // Process category stats
    const categoryCount: { [key: string]: number } = {}
    if (categoryStats) {
      categoryStats.forEach((conv) => {
        const categoryName = (conv as unknown as {
          chat_prompt?: { video?: { category?: { name?: string } | null } | null } | null
        })?.chat_prompt?.video?.category?.name
        if (categoryName) {
          categoryCount[categoryName] = (categoryCount[categoryName] || 0) + 1
        }
      })
    }

    // Process status stats
    const statusCount: { [key: string]: number } = {}
    conversationCount.forEach((conv: { status: string }) => {
      statusCount[conv.status] = (statusCount[conv.status] || 0) + 1
    })

    const totalConversations = conversationCount.length
    const activeConversations = statusCount['active'] || 0
    const totalMessages = messageCount.length

    return {
      totalConversations,
      activeConversations,
      totalMessages,
      averageMessagesPerConversation: totalConversations > 0 ? totalMessages / totalConversations : 0,
      conversationsByCategory: Object.entries(categoryCount).map(([category_name, count]) => ({
        category_name,
        count
      })),
      conversationsByStatus: Object.entries(statusCount).map(([status, count]) => ({
        status,
        count
      }))
    }
  }

  static async updateConversationStatus(
    conversationId: string,
    status: 'active' | 'archived' | 'deleted'
  ): Promise<Conversation> {
    const { data, error } = await supabase
      .from('conversations')
      .update({ status })
      .eq('id', conversationId)
      .select()
      .single()

    if (error) {
      console.error('Error updating conversation status:', error)
      throw new Error('会話ステータスの更新に失敗しました')
    }

    return data
  }

  static async updateConversationTitle(
    conversationId: string,
    title: string
  ): Promise<Conversation> {
    const { data, error } = await supabase
      .from('conversations')
      .update({ title })
      .eq('id', conversationId)
      .select()
      .single()

    if (error) {
      console.error('Error updating conversation title:', error)
      throw new Error('会話タイトルの更新に失敗しました')
    }

    return data
  }

  static async deleteConversationMessages(conversationId: string): Promise<void> {
    const { error } = await supabase
      .from('conversation_messages')
      .delete()
      .eq('conversation_id', conversationId)

    if (error) {
      console.error('Error deleting conversation messages:', error)
      throw new Error('会話メッセージの削除に失敗しました')
    }
  }

  static async getUserRecentConversations(
    userId: string,
    limit?: number
  ): Promise<ConversationWithDetails[]> {
    // Fetch one extra conversation to account for potential generic chat
    const fetchLimit = limit ? limit + 1 : undefined

    // Try a simpler query first to avoid complex join issues
    let query = supabase
      .from('conversations')
      .select(`
        *,
        users!user_id(id, name, email),
        chat_prompts!chat_prompt_id(
          id,
          title,
          is_generic,
          model,
          temperature,
          max_tokens,
          video:videos!video_id(
            id,
            title,
            category,
            category_id,
            category:categories!category_id(id, name, slug)
          )
        )
      `)
      .eq('user_id', userId)
      .eq('status', 'active')
      .order('updated_at', { ascending: false })

    if (fetchLimit) {
      query = query.limit(fetchLimit)
    }

    const { data, error } = await query

    if (error) {
      console.error('Error fetching user recent conversations:', error)
      console.error('Error details:', JSON.stringify(error, null, 2))
      
      // Fallback to an even simpler query if the complex one fails
      console.log('Trying fallback query...')
      let fallbackQuery = supabase
        .from('conversations')
        .select(`
          *,
          chat_prompts!chat_prompt_id(is_generic)
        `)
        .eq('user_id', userId)
        .eq('status', 'active')
        .order('updated_at', { ascending: false })

      if (fetchLimit) {
        fallbackQuery = fallbackQuery.limit(fetchLimit)
      }

      const { data: fallbackData, error: fallbackError } = await fallbackQuery

      if (fallbackError) {
        console.error('Fallback query also failed:', fallbackError)
        throw new Error(`最近の会話の取得に失敗しました: ${fallbackError.message || 'Unknown error'}`)
      }

      const filteredFallbackData = (fallbackData || [])
        .slice(0, limit || undefined)

      // Return the basic data without nested relations for now
      return filteredFallbackData.map(conv => ({
        ...conv,
        user: { id: userId, name: 'User', email: '' },
        chat_prompt: {
          id: conv.chat_prompt_id || '',
          title: 'Chat Prompt',
          is_generic: false,
          // The fallback query doesn't join the prompt's AI settings, so defer to
          // the system default rather than pinning these chats to a stale model.
          model: 'default',
          category: { id: '', name: 'General', slug: 'general' }
        }
      })) as ConversationWithDetails[]
    }

    // Transform the data to match the expected interface structure
    const transformedData = data.map(item => ({
      ...item,
      user: item.users || { id: userId, name: 'User', email: '' },
      chat_prompt: {
        id: item.chat_prompts?.id || '',
        title: item.chat_prompts?.title || 'Chat Prompt',
        video_id: item.chat_prompts?.video?.id || null,
        is_generic: item.chat_prompts?.is_generic ?? false,
        model: item.chat_prompts?.model || 'default',
        temperature: item.chat_prompts?.temperature,
        max_tokens: item.chat_prompts?.max_tokens,
        category: {
          id: item.chat_prompts?.video?.category?.id || '',
          name: item.chat_prompts?.video?.category?.name || 'General',
          slug: item.chat_prompts?.video?.category?.slug || 'general'
        }
      }
    }))

    return transformedData.slice(0, limit || undefined) as ConversationWithDetails[]
  }

  static async getLatestResponseId(conversationId: string): Promise<string | null> {
    const { data, error } = await supabase
      .from('conversation_messages')
      .select('openai_response_id, metadata')
      .eq('conversation_id', conversationId)
      .eq('role', 'assistant')
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle()

    if (error) {
      console.error('Error fetching latest response ID:', error)
      return null
    }

    if (!data) {
      return null // No assistant messages found
    }

    // First try the dedicated column
    if (data?.openai_response_id) {
      return data.openai_response_id
    }

    // Fallback to reading it out of metadata, for rows written before the dedicated
    // column existed. metadata is jsonb, so the client hands back a parsed object --
    // running JSON.parse on it threw and made this fallback dead code.
    if (data?.metadata) {
      try {
        const metadata = (typeof data.metadata === 'string'
          ? JSON.parse(data.metadata)
          : data.metadata) as { openaiResponseId?: unknown }
        return typeof metadata?.openaiResponseId === 'string' ? metadata.openaiResponseId : null
      } catch (parseError) {
        console.warn('Could not parse message metadata:', parseError)
        return null
      }
    }

    return null
  }
}

export type { 
  Conversation, 
  ConversationMessage
}